import { useEffect } from "react";

/**
 * Per-route `<head>` management for the SPA.
 *
 * Because this is a client-rendered SPA, `index.html` ships a single static `<head>` that is served
 * for EVERY route. This hook lets each screen override the bits that must differ per page — the
 * document title and the `robots` directive — once React has rendered.
 *
 * IMPORTANT — what this does and does NOT cover:
 *  - ✅ Browser tab/bookmark title, and Googlebot (which executes JS and reads the rendered head).
 *  - ❌ Non-JS link-preview scrapers (iMessage, WhatsApp, Slack, Facebook, X). Those read ONLY the
 *       static HTML and never see these runtime changes. Correct per-URL Open Graph cards require
 *       prerendering / a Cloudflare Pages Function injecting tags at the edge (tracked follow-up).
 *
 * Robots policy: the static default is indexable so the homepage (`/`, the page we WANT ranked) is
 * never accidentally hidden. Private routes opt OUT by passing `robots: "noindex"`.
 */
export type RobotsDirective = "index" | "noindex";

interface DocumentHeadOptions {
  title: string;
  description?: string;
  robots?: RobotsDirective;
}

function upsertMeta(name: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function useDocumentHead({ title, description, robots = "index" }: DocumentHeadOptions): void {
  useEffect(() => {
    document.title = title;
    upsertMeta("robots", robots === "noindex" ? "noindex, nofollow" : "index, follow");
    if (description !== undefined) upsertMeta("description", description);
  }, [title, description, robots]);
}
